import { CheckIcon, ClockIcon } from "@heroicons/react/24/outline";
import { PhoneNumberE164, TaskTypeId } from "@sp-crm/core";
import { ActivityTargetChips } from "components/tasks/activity-chip";
import { CompletingBlock } from "components/ui/list-motion";
import { StatusLine } from "components/ui/status-line";
import { VisitParent } from "components/visits/types";
import React from "react";
import {
    AgendaAddContactPhone,
    AgendaCallAction,
    AgendaEmailAction,
    AgendaProvisionPhone,
    AgendaTextAction,
    AgendaTouchpointActions,
    AgendaVisitAction,
} from "./agenda-actions";
import { AgendaTouchpoint, resolveTouchpointActions } from "./marketing-plan-agenda";
import { describeCadence, touchpointStatusLabel } from "./marketing-plan-format";

export type TouchpointRowView = AgendaTouchpoint;

interface TouchpointRowProps {
    touchpoint: TouchpointRowView;
    parent: VisitParent;
    email: string | null;
    emailOptedOut: boolean;
    phone: PhoneNumberE164 | null;
    hasPhoneLine: boolean;
    onSendEmail: (activityTypeId: TaskTypeId | null) => void;
}

export const TouchpointRow: React.FC<TouchpointRowProps> = props => {
    const { touchpoint } = props;
    return (
        <CompletingBlock
            isDone={touchpoint.state === "satisfied"}
            className="flex flex-col gap-2 py-3">
            <div className="text-sm">
                <div className="flex flex-wrap items-center gap-1 font-medium text-gray-700">
                    <ActivityTargetChips targets={touchpoint.targets} />
                    {describeCadence(touchpoint.intervalCount, touchpoint.intervalUnit)}
                </div>
                <TouchpointRowStatus touchpoint={touchpoint} />
            </div>
            {touchpoint.state === "due" ? <TouchpointRowActions {...props} /> : null}
        </CompletingBlock>
    );
};

export const TouchpointRowStatus: React.FC<{ touchpoint: TouchpointRowView }> = ({
    touchpoint,
}) => {
    const label = touchpointStatusLabel(touchpoint);
    if (touchpoint.state === "snoozed") {
        return (
            <StatusLine tone="muted">
                <ClockIcon className="h-3.5 w-3.5 flex-shrink-0" />
                {label}
            </StatusLine>
        );
    }
    if (touchpoint.state === "satisfied") {
        return (
            <StatusLine tone="positive">
                <CheckIcon className="h-3.5 w-3.5 flex-shrink-0" />
                {label}
            </StatusLine>
        );
    }
    return <StatusLine tone="muted">{label}</StatusLine>;
};

const TouchpointRowActions: React.FC<TouchpointRowProps> = ({
    touchpoint,
    parent,
    email,
    emailOptedOut,
    phone,
    hasPhoneLine,
    onSendEmail,
}) => {
    const actions = resolveTouchpointActions({
        targets: touchpoint.targets,
        email,
        emailOptedOut,
        hasPhone: !!phone,
        hasPhoneLine,
    });

    return (
        <div className="flex flex-wrap justify-end gap-2">
            {actions.email ? (
                <AgendaEmailAction
                    state={actions.email}
                    parent={parent}
                    targets={touchpoint.targets}
                    onSendEmail={onSendEmail}
                />
            ) : null}
            {actions.addContactPhone ? <AgendaAddContactPhone parent={parent} /> : null}
            {actions.provisionPhoneLine ? <AgendaProvisionPhone /> : null}
            {actions.call && phone ? (
                <AgendaCallAction
                    phone={phone}
                    entityId={parent.id}
                    targets={touchpoint.targets}
                />
            ) : null}
            {actions.text && phone ? (
                <AgendaTextAction
                    phone={phone}
                    targets={touchpoint.targets}
                    entityId={parent.id}
                />
            ) : null}
            {actions.visit ? (
                <AgendaVisitAction parent={parent} targets={touchpoint.targets} />
            ) : null}
            <AgendaTouchpointActions
                parent={parent}
                touchpointId={touchpoint.touchpointId}
                targets={touchpoint.targets}
            />
        </div>
    );
};
